import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { userState, vendorState } from '../state';
import type { VendorModule as Module, User, Vendor } from '../lib/graphql';
import {
  canWrite,
  isAdmin,
  isProcessor,
  isProducer,
  isReadOnlyProcessor,
  isVendorOwner,
} from '../lib/userHelpers';
import { ModuleId } from '../lib/modules/module-id.enum';

interface UserStateContextValue {
  user: User | undefined;
  vendor: Vendor | undefined;
  isAdmin: boolean;
  isProcessor: boolean;
  isProducer: boolean;
  isVendorOwner: boolean;
  isReadOnlyProcessor: boolean;
  /** Whether the user may perform write actions; false for read-only accounts. */
  canWrite: boolean;
  isActiveModule: (moduleId: ModuleId) => boolean;
  getModuleConfig: <T = Record<string, unknown>>(
    moduleId: ModuleId,
  ) => T | null;
}

const UserStateContext = createContext<UserStateContextValue | undefined>(
  undefined,
);

interface UserStateProviderProps {
  children: ReactNode;
}

export function UserStateProvider({ children }: UserStateProviderProps) {
  const user = useRecoilValue(userState);
  const vendor = useRecoilValue(vendorState);

  const contextValue: UserStateContextValue = useMemo(
    () => ({
      user,
      vendor,
      isAdmin: isAdmin(user),
      isProcessor: isProcessor(user),
      isProducer: isProducer(user),
      isVendorOwner: isVendorOwner(user),
      isReadOnlyProcessor: isReadOnlyProcessor(user),
      canWrite: canWrite(user),
      isActiveModule: (moduleId: ModuleId) =>
        user?.active_vendor?.modules?.some(
          (m: Module) => m?.moduleType === moduleId && m?.active,
        ) ?? false,
      getModuleConfig: <T = Record<string, unknown>,>(
        moduleId: ModuleId,
      ): T | null => {
        const module = user?.active_vendor?.modules?.find(
          (m: Module) => m?.moduleType === moduleId,
        );
        return (module?.settings as T) ?? null;
      },
    }),
    [user, vendor],
  );

  return (
    <UserStateContext.Provider value={contextValue}>
      {children}
    </UserStateContext.Provider>
  );
}

export function useUserState(): UserStateContextValue {
  const context = useContext(UserStateContext);
  if (context === undefined) {
    throw new Error('useUserState must be used within a UserStateProvider');
  }
  return context;
}
